import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import {
  ClaudeSidecarClient,
  ConnectionStatus,
  SidecarEvent,
  SidecarPermissionRequest,
} from "../../services/claudeSidecar";

export interface AgentChatProps {
  /** Websocket to connect to: the Rocket proxy, or a direct sidecar (island). */
  wsUrl: string;
  /**
   * Absolute repo path. Omit when connecting through the Rocket proxy — it
   * injects the path. Required for a direct sidecar connection (island test).
   */
  repoPath?: string;
  /** Tools auto-approved without prompting. */
  allowedTools?: string[];
}

type ChatItem =
  | { kind: "user"; text: string }
  | { kind: "assistant"; text: string }
  | { kind: "tool"; name: string; input: Record<string, unknown> }
  | { kind: "system"; text: string }
  | { kind: "result"; text: string }
  | { kind: "error"; text: string };

const statusColor: Record<ConnectionStatus, "default" | "success" | "warning" | "error"> = {
  connecting: "warning",
  open: "success",
  closed: "default",
  error: "error",
};

export default function AgentChat({ wsUrl, repoPath, allowedTools }: AgentChatProps) {
  const [items, setItems] = useState<ChatItem[]>([]);
  const [pending, setPending] = useState<SidecarPermissionRequest[]>([]);
  const [status, setStatus] = useState<ConnectionStatus>("connecting");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [meta, setMeta] = useState<{ model?: string; apiKeySource?: string }>({});

  const clientRef = useRef<ClaudeSidecarClient | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const push = (item: ChatItem) => setItems((prev) => [...prev, item]);

  useEffect(() => {
    const client = new ClaudeSidecarClient(handleEvent, setStatus, wsUrl);
    clientRef.current = client;
    client.connect();
    return () => client.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsUrl, repoPath]);

  // Auto-scroll to the newest content.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [items, pending]);

  function handleEvent(e: SidecarEvent) {
    switch (e.type) {
      case "ready":
        clientRef.current?.start({
          repoPath, // omitted through the proxy; it injects the path
          allowedTools: allowedTools ?? ["Read", "Glob", "Grep"],
          permissionMode: "default",
        });
        break;
      case "system":
        setMeta({ model: e.model, apiKeySource: e.apiKeySource });
        push({
          kind: "system",
          text: `Session ready — ${e.model} · auth=${e.apiKeySource} · ${e.cwd}`,
        });
        break;
      case "assistant":
        push({ kind: "assistant", text: e.text });
        break;
      case "tool_use":
        push({ kind: "tool", name: e.name, input: e.input });
        break;
      case "permission_request":
        setPending((prev) => [...prev, e]);
        break;
      case "result":
        setBusy(false);
        push({
          kind: "result",
          text:
            `${e.isError ? "⚠️ " : "✓ "}${e.subtype} · $${(e.costUsd ?? 0).toFixed(4)}` +
            ` · ${e.numTurns} turns`,
        });
        break;
      case "error":
        setBusy(false);
        push({ kind: "error", text: e.message });
        break;
      case "closed":
        setBusy(false);
        break;
      default:
        break;
    }
  }

  function handleSend() {
    const text = draft.trim();
    if (!text || status !== "open") return;
    push({ kind: "user", text });
    clientRef.current?.input(text);
    setDraft("");
    setBusy(true);
  }

  function answer(id: string, behavior: "allow" | "deny") {
    clientRef.current?.respondPermission(id, behavior, {
      message: behavior === "deny" ? "Denied by user." : undefined,
    });
    setPending((prev) => prev.filter((p) => p.id !== id));
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Header / status */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Chip size="small" label={status} color={statusColor[status]} />
        {meta.model && <Chip size="small" variant="outlined" label={meta.model} />}
        {meta.apiKeySource && (
          <Chip
            size="small"
            variant="outlined"
            color={/api.?key/i.test(meta.apiKeySource) ? "error" : "default"}
            label={`auth: ${meta.apiKeySource}`}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" noWrap>
          {repoPath ?? wsUrl}
        </Typography>
      </Box>

      {/* Transcript */}
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          p: 1.5,
          backgroundColor: "#fafafa",
        }}
      >
        {items.map((item, i) => (
          <MessageRow key={i} item={item} />
        ))}

        {pending.map((p) => (
          <Paper
            key={p.id}
            variant="outlined"
            sx={{ p: 1.5, my: 1, borderColor: "#FF9933", backgroundColor: "#fff8ef" }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Permission requested: <code>{p.name}</code>
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                mb: 1,
                p: 1,
                fontSize: 12,
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                backgroundColor: "#f0f0f0",
                borderRadius: 1,
              }}
            >
              {JSON.stringify(p.input, null, 2)}
            </Box>
            {p.blockedPath && (
              <Typography variant="caption" color="error">
                Blocked path: {p.blockedPath}
              </Typography>
            )}
            <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
              <Button size="small" variant="contained" onClick={() => answer(p.id, "allow")}>
                Allow
              </Button>
              <Button size="small" variant="outlined" onClick={() => answer(p.id, "deny")}>
                Deny
              </Button>
            </Box>
          </Paper>
        ))}

        {busy && pending.length === 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, my: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">
              Claude is working…
            </Typography>
          </Box>
        )}
      </Box>

      {/* Composer */}
      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder={status === "open" ? "Message the agent…" : "Connecting…"}
          value={draft}
          disabled={status !== "open"}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <Button variant="contained" onClick={handleSend} disabled={status !== "open" || !draft.trim()}>
          Send
        </Button>
      </Box>
    </Box>
  );
}

function MessageRow({ item }: { item: ChatItem }) {
  switch (item.kind) {
    case "user":
      return (
        <Box sx={{ my: 1, textAlign: "right" }}>
          <Paper
            sx={{
              display: "inline-block",
              p: 1,
              px: 1.5,
              backgroundColor: "#000",
              color: "#fff",
              maxWidth: "85%",
              textAlign: "left",
              whiteSpace: "pre-wrap",
            }}
          >
            {item.text}
          </Paper>
        </Box>
      );
    case "assistant":
      return (
        <Box sx={{ my: 1 }}>
          <Paper
            variant="outlined"
            sx={{ display: "inline-block", p: 1, px: 1.5, maxWidth: "85%", whiteSpace: "pre-wrap" }}
          >
            {item.text}
          </Paper>
        </Box>
      );
    case "tool":
      return (
        <Typography
          variant="caption"
          component="div"
          sx={{ my: 0.5, color: "text.secondary", fontFamily: "monospace" }}
        >
          🔧 {item.name}({JSON.stringify(item.input)})
        </Typography>
      );
    case "system":
      return (
        <Typography variant="caption" component="div" sx={{ my: 0.5, color: "text.secondary" }}>
          {item.text}
        </Typography>
      );
    case "result":
      return (
        <Typography variant="caption" component="div" sx={{ my: 0.5, color: "success.main" }}>
          {item.text}
        </Typography>
      );
    case "error":
      return (
        <Typography variant="caption" component="div" sx={{ my: 0.5, color: "error.main" }}>
          ⚠️ {item.text}
        </Typography>
      );
    default:
      return null;
  }
}
